// src/features/quotation/utils/quotationHelpers.js
/**
 * ============================================
 * QUOTATION MODULE - UTILITY FUNCTIONS
 * ============================================
 * This file contains helper functions for
 * quotation calculations, formatting, and
 * data manipulation.
 *
 * @version 1.0.0
 * @author Hashtag Photography Team
 * @lastUpdated 2024-01-24
 * ============================================
 */

import {
  QUOTATION_STATUS,
  QUOTATION_STATUS_LABELS,
  QUOTATION_STATUS_COLORS,
  QUOTATION_STATUS_ICONS,
  QUOTATION_STATUS_TRANSITIONS,
  DISCOUNT_TYPES,
  CURRENCY_OPTIONS,
} from "../types/quotationTypes";

/**
 * Calculate quotation totals
 *
 * @param {Array} items - Array of quotation items
 * @param {number} discount - Discount amount or percentage
 * @param {string} discountType - 'percentage' or 'fixed'
 * @param {number} taxRate - Tax rate percentage
 * @returns {Object} Calculated totals
 */
export const calculateTotals = (
  items,
  discount = 0,
  discountType = DISCOUNT_TYPES.PERCENTAGE,
  taxRate = 0,
) => {
  // Calculate subtotal
  let subtotal = 0;
  items.forEach((item) => {
    const itemTotal = item.quantity * item.unitPrice;
    subtotal += itemTotal;
  });

  // Calculate discount
  let discountAmount = 0;
  if (discountType === DISCOUNT_TYPES.PERCENTAGE) {
    discountAmount = subtotal * (discount / 100);
  } else {
    discountAmount = discount;
  }

  // Calculate tax
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = taxableAmount * (taxRate / 100);

  // Calculate total
  const total = subtotal - discountAmount + taxAmount;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discountAmount * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
};

/**
 * Format currency
 *
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (USD, EUR, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = "USD") => {
  const currencyOption = CURRENCY_OPTIONS.find((c) => c.code === currency);
  const symbol = currencyOption?.symbol || "$";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Generate quotation number
 *
 * @param {number} sequence - Current sequence number
 * @param {string} prefix - Prefix for the number (default: 'QUOT')
 * @returns {string} Generated quotation number
 */
export const generateQuotationNumber = (sequence, prefix = "QUOT") => {
  return `${prefix}-${String(sequence).padStart(4, "0")}`;
};

/**
 * Check if quotation is expired
 *
 * @param {string} validUntil - Valid until date (ISO string)
 * @returns {boolean} True if expired
 */
export const isQuotationExpired = (validUntil) => {
  return new Date(validUntil) < new Date();
};

/**
 * Check if quotation is expiring soon
 *
 * @param {string} validUntil - Valid until date (ISO string)
 * @param {number} days - Number of days threshold (default: 7)
 * @returns {boolean} True if expiring soon
 */
export const isQuotationExpiringSoon = (validUntil, days = 7) => {
  const expiryDate = new Date(validUntil);
  const now = new Date();
  const diffTime = expiryDate - now;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays > 0 && diffDays <= days;
};

/**
 * Get days until expiry
 *
 * @param {string} validUntil - Valid until date (ISO string)
 * @returns {number} Number of days until expiry (negative if expired)
 */
export const getDaysUntilExpiry = (validUntil) => {
  const expiryDate = new Date(validUntil);
  const now = new Date();
  const diffTime = expiryDate - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get status label
 *
 * @param {string} status - Quotation status
 * @returns {string} Human-readable status label
 */
export const getStatusLabel = (status) => {
  return QUOTATION_STATUS_LABELS[status] || status;
};

/**
 * Get status color
 *
 * @param {string} status - Quotation status
 * @returns {string} Color code for the status
 */
export const getStatusColor = (status) => {
  return QUOTATION_STATUS_COLORS[status] || "#d9d9d9";
};

/**
 * Get status icon
 *
 * @param {string} status - Quotation status
 * @returns {string} Icon/emoji for the status
 */
export const getStatusIcon = (status) => {
  return QUOTATION_STATUS_ICONS[status] || "📄";
};

/**
 * Check if status transition is valid
 *
 * @param {string} currentStatus - Current status
 * @param {string} newStatus - New status
 * @returns {boolean} True if transition is allowed
 */
export const isValidStatusTransition = (currentStatus, newStatus) => {
  const allowedTransitions = QUOTATION_STATUS_TRANSITIONS[currentStatus] || [];
  return allowedTransitions.includes(newStatus);
};

/**
 * Filter quotations by various criteria
 *
 * @param {Array} quotations - Array of quotations
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered quotations
 */
export const filterQuotations = (quotations, filters = {}) => {
  let result = [...quotations];

  // Status filter
  if (filters.status && filters.status !== "all") {
    result = result.filter((q) => q.status === filters.status);
  }

  // Search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    result = result.filter(
      (q) =>
        q.number.toLowerCase().includes(searchLower) ||
        q.clientName.toLowerCase().includes(searchLower) ||
        (q.title && q.title.toLowerCase().includes(searchLower)),
    );
  }

  // Date range filter
  if (filters.dateRange?.start) {
    const startDate = new Date(filters.dateRange.start);
    result = result.filter((q) => new Date(q.date) >= startDate);
  }
  if (filters.dateRange?.end) {
    const endDate = new Date(filters.dateRange.end);
    result = result.filter((q) => new Date(q.date) <= endDate);
  }

  // Client filter
  if (filters.clientId && filters.clientId !== "all") {
    result = result.filter((q) => q.clientId === filters.clientId);
  }

  // Amount filter
  if (filters.minAmount) {
    result = result.filter((q) => q.total >= filters.minAmount);
  }
  if (filters.maxAmount) {
    result = result.filter((q) => q.total <= filters.maxAmount);
  }

  // Tags filter
  if (filters.tags && filters.tags.length > 0) {
    result = result.filter(
      (q) => q.tags && filters.tags.some((tag) => q.tags.includes(tag)),
    );
  }

  return result;
};

/**
 * Sort quotations
 *
 * @param {Array} quotations - Array of quotations
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - 'asc' or 'desc'
 * @returns {Array} Sorted quotations
 */
export const sortQuotations = (
  quotations,
  sortBy = "date",
  sortOrder = "desc",
) => {
  const sorted = [...quotations];

  sorted.sort((a, b) => {
    let valueA = a[sortBy];
    let valueB = b[sortBy];

    // Handle different types
    if (
      sortBy === "date" ||
      sortBy === "createdAt" ||
      sortBy === "validUntil"
    ) {
      valueA = new Date(valueA).getTime();
      valueB = new Date(valueB).getTime();
    } else if (sortBy === "total") {
      valueA = parseFloat(valueA);
      valueB = parseFloat(valueB);
    } else if (sortBy === "clientName") {
      valueA = (valueA || "").toLowerCase();
      valueB = (valueB || "").toLowerCase();
    } else if (sortBy === "status") {
      valueA = QUOTATION_STATUS_LABELS[valueA] || valueA;
      valueB = QUOTATION_STATUS_LABELS[valueB] || valueB;
    }

    // Compare
    if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
    if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
};

/**
 * Calculate quotation statistics
 *
 * @param {Array} quotations - Array of quotations
 * @returns {Object} Statistics object
 */
export const calculateQuotationStats = (quotations) => {
  const total = quotations.length;

  const statusCounts = {
    draft: 0,
    sent: 0,
    viewed: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
    converted: 0,
  };

  let totalValue = 0;
  let approvedCount = 0;
  let respondedCount = 0;

  quotations.forEach((q) => {
    // Count by status
    if (statusCounts[q.status] !== undefined) {
      statusCounts[q.status]++;
    }

    // Total value
    totalValue += q.total || 0;

    // Response rate
    if (q.status === "approved" || q.status === "rejected") {
      respondedCount++;
    }

    // Conversion rate
    if (q.status === "approved") {
      approvedCount++;
    }
  });

  const conversionRate = total > 0 ? (approvedCount / total) * 100 : 0;
  const responseRate = total > 0 ? (respondedCount / total) * 100 : 0;
  const averageValue = total > 0 ? totalValue / total : 0;

  return {
    total,
    ...statusCounts,
    totalValue: Math.round(totalValue * 100) / 100,
    averageValue: Math.round(averageValue * 100) / 100,
    conversionRate: Math.round(conversionRate * 100) / 100,
    responseRate: Math.round(responseRate * 100) / 100,
  };
};

/**
 * Generate quotation PDF data
 *
 * @param {Object} quotation - Quotation object
 * @returns {Object} PDF data structure
 */
export const generateQuotationPDFData = (quotation) => {
  const items = quotation.items.map((item) => ({
    description: item.description,
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    total: item.quantity * item.unitPrice,
  }));

  return {
    title: quotation.title || "Quotation",
    number: quotation.number,
    date: quotation.date,
    validUntil: quotation.validUntil,
    clientName: quotation.clientName,
    clientEmail: quotation.clientEmail,
    clientPhone: quotation.clientPhone,
    clientCompany: quotation.clientCompany,
    clientAddress: quotation.clientAddress,
    items: items,
    subtotal: quotation.subtotal,
    discount: quotation.discount,
    discountType: quotation.discountType,
    discountAmount: quotation.discountAmount,
    taxRate: quotation.taxRate,
    taxAmount: quotation.taxAmount,
    total: quotation.total,
    paymentTerms: quotation.paymentTerms,
    notes: quotation.notes,
    termsAndConditions: quotation.termsAndConditions,
    currency: quotation.currency,
    status: quotation.status,
  };
};

/**
 * Validate quotation data
 *
 * @param {Object} data - Quotation data to validate
 * @returns {Object} { isValid: boolean, errors: Array }
 */
export const validateQuotationData = (data) => {
  const errors = [];

  // Validate title
  if (!data.title || data.title.length < 3) {
    errors.push({
      field: "title",
      message: "Title must be at least 3 characters",
    });
  }

  // Validate client name
  if (!data.clientName || data.clientName.length < 2) {
    errors.push({ field: "clientName", message: "Client name is required" });
  }

  // Validate client email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!data.clientEmail || !emailRegex.test(data.clientEmail)) {
    errors.push({ field: "clientEmail", message: "Valid email is required" });
  }

  // Validate date
  if (!data.date) {
    errors.push({ field: "date", message: "Date is required" });
  }

  // Validate valid until
  if (!data.validUntil) {
    errors.push({
      field: "validUntil",
      message: "Valid until date is required",
    });
  } else if (new Date(data.validUntil) < new Date(data.date)) {
    errors.push({
      field: "validUntil",
      message: "Valid until date must be after the date",
    });
  }

  // Validate items
  if (!data.items || data.items.length === 0) {
    errors.push({ field: "items", message: "At least one item is required" });
  } else {
    data.items.forEach((item, index) => {
      if (!item.description || item.description.length < 2) {
        errors.push({
          field: `items[${index}].description`,
          message: "Item description is required",
        });
      }
      if (!item.quantity || item.quantity < 1) {
        errors.push({
          field: `items[${index}].quantity`,
          message: "Quantity must be at least 1",
        });
      }
      if (!item.unitPrice || item.unitPrice < 0) {
        errors.push({
          field: `items[${index}].unitPrice`,
          message: "Unit price must be valid",
        });
      }
    });
  }

  // Validate discount
  if (data.discount < 0) {
    errors.push({ field: "discount", message: "Discount cannot be negative" });
  }

  // Validate tax rate
  if (data.taxRate < 0 || data.taxRate > 100) {
    errors.push({
      field: "taxRate",
      message: "Tax rate must be between 0 and 100",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
